import { ContextApp } from '@lmig/dotcom-aspect-components/ContextApp';
import fetch from '@lmig/dotcom-aspect-helpers/isoApiCall';
import Logger from '@lmig/dotcom-utilities-helpers/logger';
import urlHelper from '@lmig/dotcom-aspect-helpers/urlHelper';
import { useContext, useEffect } from 'react';

const logger = new Logger('client:hooks:useLocation');

/**
  useLocation always returns an object with properties state, city and zipCode

  Properties will be:
    - undefined
      * before a response is returned from the server
    - null
      * when an error response is return from the server
      * when a response is returned but no header information is available
    - string
      * when proper header information is available from the response
*/
const useLocation = (html = false, useZip = true) => {
  const { location, setLocation, hasCheckedLocation, setHasCheckedLocation } = useContext(ContextApp);

  useEffect(() => {
    if (!location?.state && !hasCheckedLocation) {
      const url = urlHelper("/health");
      fetch(url.toString(), { method: 'HEAD' }).then(r => {
        const state = r.headers.get('user_statecode');
        const city = r.headers.get('user_city');
        const zipCode = r.headers.get('user_zipcode');
        const latitude = r.headers.get('user_latitude');
        const longitude = r.headers.get('user_longitude');
        logger.debug(`Setting location - state: ${state}, city: ${city}, and zipCode: ${zipCode} with lat: ${latitude} and lng: ${longitude}`);

        if (setHasCheckedLocation) {
          setHasCheckedLocation(true);
        }

        if (state && city && zipCode) {
          setLocation({
            state,
            city,
            latitude,
            longitude,
            zipCode: zipCode.split('-')[0].split('+')[0],
          });
        }
      });
    }
  }, [setHasCheckedLocation, hasCheckedLocation, location, setLocation]);

  useEffect(() => {
    function success({ coords: { latitude, longitude } }) {
      logger.debug(`HTML5 Geolocation is enabled`);

      if (!useZip) {
        setLocation({
          latitude,
          longitude,
        });
        setHasCheckedLocation(true);
      }

      if (useZip) {
        const url = urlHelper(`/api/v1/geocode?latitude=${latitude}&longitude=${longitude}`);
        fetch(url.toString()).then(async r => {
          const { zipcode: zipCode, city, state } = await r.json();

          setHasCheckedLocation(true);

          if (zipCode && city && state) {
            logger.debug(`Setting location from geolocation - state: ${state}, city: ${city}, and zipCode: ${zipCode} with lat: ${latitude} and lng: ${longitude}`);
            setLocation({
              state,
              city,
              zipCode,
              latitude,
              longitude,
            });
          }
        });
      }
    }

    function error(e) {
      logger.debug(`HTML5 Geolocation is disabled`);
      logger.error(e);
    }

    if (html && hasCheckedLocation && !location?.state) {
      try {
        navigator.geolocation.getCurrentPosition(success, error, {
          enableHighAccuracy: true,
        });
      } catch (e) {
        logger.debug(`HTML5 Geolocation failed: ${e.toString()}`);
      }
    }
  }, [html, hasCheckedLocation, setLocation, useZip, location, setHasCheckedLocation]);

  return { location, setLocation };
};

export default useLocation;
